import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Package, Truck, Copy } from "lucide-react";
import { toast } from "sonner";
import { formatINR } from "@/data/products";

const ORDERS_KEY = "innayaa_orders_v1";

export default function OrderConfirmed() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    try {
      const list = JSON.parse(localStorage.getItem(ORDERS_KEY) || "[]");
      setOrder(list.find((o) => o.id === id) || null);
    } catch (e) {
      setOrder(null);
    }
  }, [id]);

  const copyId = () => {
    if (!order) return;
    navigator.clipboard.writeText(order.id).then(
      () => toast.success("Order ID copied"),
      () => toast.error("Couldn't copy — please try again")
    );
  };

  if (!order) {
    return (
      <div data-testid="order-not-found" className="max-w-[600px] mx-auto px-5 sm:px-6 py-24 md:py-32 text-center">
        <p className="overline">Order not found</p>
        <h1 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-[#2A2626]">
          We couldn&rsquo;t find this order.
        </h1>
        <p className="mt-4 text-[#5C564F]">
          If you just placed an order, please refresh. Otherwise, feel free to browse the collection.
        </p>
        <Link
          to="/shop"
          className="mt-10 inline-block bg-[#2A2626] text-[#FAF8F5] px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
        >
          Browse the collection
        </Link>
      </div>
    );
  }

  const placedAt = new Date(order.placedAt);

  return (
    <div data-testid="order-confirmed-page" className="bg-[#FAF8F5]">
      <section className="max-w-[900px] mx-auto px-5 sm:px-6 md:px-10 pt-12 md:pt-20 pb-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#2A2626] text-[#FAF8F5] flex items-center justify-center">
          <Check strokeWidth={1.5} size={26} />
        </div>
        <p className="overline mt-6">Order placed</p>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#2A2626] leading-tight">
          Thank you, {order.customer.fullName.split(" ")[0]}.
        </h1>
        <p className="mt-5 text-[15px] md:text-base text-[#5C564F] max-w-lg mx-auto leading-relaxed">
          Your Innayaa is being lovingly prepared. We&rsquo;ll be in touch when your
          parcel is dispatched.
        </p>
        <div className="mt-8 inline-flex items-center gap-3 bg-[#F0EBE1] px-5 py-3">
          <span className="overline">Order ID</span>
          <span data-testid="order-id" className="font-serif text-lg text-[#2A2626]">
            {order.id}
          </span>
          <button
            onClick={copyId}
            data-testid="copy-order-id"
            className="text-[#5C564F] hover:text-[#2A2626]"
            aria-label="Copy order ID"
          >
            <Copy size={15} strokeWidth={1.5} />
          </button>
        </div>
      </section>

      <section className="max-w-[900px] mx-auto px-5 sm:px-6 md:px-10 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-4">
          {[
            { Icon: Truck, t: "Ships in 2–3 working days" },
            { Icon: Package, t: "In our keepsake box" },
            { Icon: Check, t: "Order confirmed" },
          ].map(({ Icon, t }) => (
            <div key={t} className="bg-[#F0EBE1]/60 p-4 md:p-5 flex items-center gap-3">
              <Icon size={18} strokeWidth={1.25} className="text-[#2A2626] shrink-0" />
              <span className="text-sm text-[#2A2626]">{t}</span>
            </div>
          ))}
        </div>

        <div className="mt-10 md:mt-12 border-t border-[#DCD5CA] pt-8 md:pt-10">
          <p className="overline">In your bag</p>
          <ul className="mt-5 divide-y divide-[#DCD5CA]">
            {order.items.map((i) => (
              <li key={i.id} data-testid={`confirmed-item-${i.slug}`} className="py-4 flex gap-4">
                <div className="w-16 h-20 bg-[#F0EBE1] shrink-0 overflow-hidden">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-[#2A2626] leading-tight">{i.name}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#5C564F]">
                    Qty {i.quantity}
                  </p>
                </div>
                <p className="text-sm text-[#2A2626] shrink-0 self-start">
                  {formatINR(i.price * i.quantity)}
                </p>
              </li>
            ))}
          </ul>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <p className="overline">Ship to</p>
              <address className="mt-3 not-italic text-sm text-[#2A2626] leading-relaxed">
                <p>{order.customer.fullName}</p>
                <p>{order.customer.address1}</p>
                {order.customer.address2 && <p>{order.customer.address2}</p>}
                <p>
                  {order.customer.city}, {order.customer.state} {order.customer.pincode}
                </p>
                <p className="mt-2 text-[#5C564F]">{order.customer.phone}</p>
                <p className="text-[#5C564F]">{order.customer.email}</p>
              </address>
            </div>
            <div>
              <p className="overline">Payment</p>
              <div className="mt-3 text-sm text-[#2A2626]">
                <p className="font-serif text-lg">Paid Online</p>
                <p className="mt-1 text-[#5C564F]">
                  Order total: <span data-testid="confirmed-total" className="text-[#2A2626]">{formatINR(order.totals.total)}</span>
                </p>
              </div>
              <dl className="mt-6 space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-[#5C564F]">Subtotal</dt>
                  <dd className="text-[#2A2626]">{formatINR(order.totals.subtotal)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#5C564F]">Shipping</dt>
                  <dd className="text-[#2A2626]">
                    {order.totals.shipping === 0 ? "Complimentary" : formatINR(order.totals.shipping)}
                  </dd>
                </div>
                <div className="flex justify-between font-serif text-lg pt-2 border-t border-[#DCD5CA]">
                  <dt>Total</dt>
                  <dd>{formatINR(order.totals.total)}</dd>
                </div>
              </dl>
            </div>
          </div>

          <p className="mt-8 text-[11px] uppercase tracking-[0.22em] text-[#5C564F]">
            Placed on {placedAt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/shop"
            data-testid="continue-shopping-link"
            className="inline-block bg-[#2A2626] text-[#FAF8F5] px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors"
          >
            Continue shopping
          </Link>
        </div>
      </section>
    </div>
  );
}
